import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn.js';

/* -------------------------------------------------------------------------- */
/* Surfaces                                                                   */
/* -------------------------------------------------------------------------- */

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('card', className)} {...props} />;
}

export function CardHeader({
  title,
  subtitle,
  action,
  icon,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
      <div className="flex items-start gap-2.5">
        {icon ? <span className="mt-0.5 text-brand-400">{icon}</span> : null}
        <div>
          <h2 className="text-[0.9375rem] font-semibold text-text">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-sm text-text-muted">{subtitle}</p> : null}
        </div>
      </div>
      {action}
    </div>
  );
}

/**
 * Tuile de compteur des vues d'accueil (maquettes backoffice et dashboard).
 *
 * La valeur est en monospace : elle change souvent en temps réel et une police
 * à largeur fixe évite que la tuile ne « saute » à chaque mise à jour.
 */
export function StatCard({
  label,
  value,
  tone = 'text-brand-400',
  hint,
}: {
  label: string;
  value: ReactNode;
  tone?: string;
  hint?: ReactNode;
}) {
  return (
    <div className="card px-5 py-4">
      <p className="label-caps">{label}</p>
      <p className={cn('mt-2 font-mono text-3xl font-semibold tabular-nums', tone)}>{value}</p>
      {hint ? <p className="mt-1 text-xs text-text-dim">{hint}</p> : null}
    </div>
  );
}

/** Paire libellé / valeur technique des vues de détail. */
export function FieldValue({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-sunken px-4 py-3">
      <p className="label-caps">{label}</p>
      <p className={cn('mt-1 break-words text-sm text-text', mono && 'font-mono')}>
        {value ?? <span className="text-text-dim">—</span>}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Badges et pastilles                                                        */
/* -------------------------------------------------------------------------- */

export function Badge({
  children,
  tone,
  className,
}: {
  children: ReactNode;
  /** Classes de tonalité issues de `tokens.ts`. */
  tone: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide',
        tone,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Dot({ className }: { className: string }) {
  return <span className={cn('inline-block size-2 shrink-0 rounded-full', className)} />;
}

/* -------------------------------------------------------------------------- */
/* Actions                                                                    */
/* -------------------------------------------------------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'gradient';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-500 text-void hover:bg-brand-400 font-semibold',
  secondary:
    'bg-surface-raised text-text ring-1 ring-border-strong hover:bg-steel/40 hover:ring-steel',
  ghost: 'text-text-muted hover:bg-surface-raised hover:text-text',
  danger: 'bg-critical/10 text-critical ring-1 ring-critical/30 hover:bg-critical/20',
  success: 'bg-success/10 text-success ring-1 ring-success/30 hover:bg-success/20',
  gradient:
    'bg-linear-to-r from-electric to-violet text-white font-semibold hover:brightness-110',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  block?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  block = false,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg transition-all',
        'disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2.5 text-sm',
        block && 'w-full',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* États                                                                      */
/* -------------------------------------------------------------------------- */

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Chargement"
      className={cn(
        'inline-block size-4 animate-spin rounded-full border-2 border-border-strong border-t-brand-500',
        className,
      )}
    />
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      <p className="text-sm font-medium text-text">{title}</p>
      {description ? <p className="max-w-md text-sm text-text-muted">{description}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <p className="text-sm font-medium text-critical">Impossible de charger les données</p>
      <p className="max-w-md text-sm text-text-muted">{message}</p>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Réessayer
        </Button>
      ) : null}
    </div>
  );
}
